import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useReleaseNotesProductId, useReleaseNotesQuery } from '../legolandApi';

// Taken from https://stackoverflow.com/questions/6122571/simple-non-secure-hash-function-for-javascript
function hash(contents: string) {
  var hash = 0;
  if (contents.length == 0) {
    return hash.toString();
  }
  for (var i = 0; i < contents.length; i++) {
    var char = contents.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString();
}

export interface ReleaseNotesStatus {
  dismissedHash: string | null;
  isDismissed: boolean;
  isRead: boolean;
  seenHash: string | null;
}

export function useReleaseNotesStatusQuery({
  productId,
}: { productId?: string } = {}) {
  productId = useReleaseNotesProductId(productId);

  const { data, error, isError, isSuccess } = useReleaseNotesQuery(
    { productId },
    { refetchInterval: 10 * 60 * 1000 },
  );

  return useQuery<ReleaseNotesStatus>(
    [
      'legoland',
      'products',
      'release-notes',
      productId,
      'status',
      data?.releaseNotes,
    ],
    () => {
      if (error) {
        throw error;
      }
      if (!productId || !data!.releaseNotes?.trim()) {
        return {
          dismissedHash: null,
          isDismissed: true,
          isRead: true,
          seenHash: null,
        };
      }
      let seenHash = localStorage.getItem(
        `legolandSdk.releaseNotes.${productId}.seenHash`,
      );
      let dismissedHash = localStorage.getItem(
        `legolandSdk.releaseNotes.${productId}.dismissedHash`,
      );

      // When no information about the status, mark the current RN as read.
      if (seenHash === null) {
        seenHash = hash(data!.releaseNotes);
        // I know that causing side effects in a function with "query" in its name isn't a good practice,
        // but I don't have any better idea on how I could implement this without increasing complexity too much.
        localStorage.setItem(
          `legolandSdk.releaseNotes.${productId}.seenHash`,
          seenHash,
        );
      }

      // When no information about the status, mark release notification as dismissed.
      if (dismissedHash === null) {
        dismissedHash = hash(data!.releaseNotes);
        localStorage.setItem(
          `legolandSdk.releaseNotes.${productId}.dismissedHash`,
          dismissedHash,
        );
      }

      const isRead = seenHash === hash(data!.releaseNotes);
      const isDismissed = isRead || dismissedHash === hash(data!.releaseNotes);

      return { dismissedHash, isDismissed, isRead, seenHash };
    },
    { enabled: isSuccess || isError },
  );
}

export function useReleaseNotesMarkReadMutation({
  productId,
}: { productId?: string } = {}) {
  const queryClient = useQueryClient();

  productId = useReleaseNotesProductId(productId);

  return useMutation(
    async ({ releaseNotes }: { releaseNotes: string }) => {
      if (!productId) {
        return;
      }
      const seenHash = hash(releaseNotes);
      localStorage.setItem(
        `legolandSdk.releaseNotes.${productId}.seenHash`,
        seenHash,
      );
      localStorage.setItem(
        `legolandSdk.releaseNotes.${productId}.dismissedHash`,
        seenHash,
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'legoland',
          'products',
          'release-notes',
          productId,
          'status',
        ]);
      },
    },
  );
}

export function useReleaseNotificationMarkDismissedMutation({
  productId,
}: { productId?: string } = {}) {
  const queryClient = useQueryClient();

  productId = useReleaseNotesProductId();

  return useMutation(
    async ({ releaseNotes }: { releaseNotes: string }) => {
      if (!productId) {
        return;
      }
      const dismissedHash = hash(releaseNotes);
      localStorage.setItem(
        `legolandSdk.releaseNotes.${productId}.dismissedHash`,
        dismissedHash,
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'legoland',
          'products',
          'release-notes',
          productId,
          'status',
        ]);
      },
    },
  );
}
